import { Delete, Edit, PlayArrow, Sync } from '@mui/icons-material';
import { Alert, Divider, IconButton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { format } from 'date-fns';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { WebmeticsMapping } from './webmetics.types';

interface Props {
  mappings: WebmeticsMapping[];
  testMapping: { result: any; open: boolean; loading: boolean; errorMessage: string | null };
  syncMappingLoading: boolean;

  handleTestMapping: (mappingId: string) => void;

  handleCloseErrorMessage: () => void;

  handleSyncMapping: (mappingId: string) => void;

  openEditMappingDialog(mapping: WebmeticsMapping): () => void;

  handleOpenDeleteDialog(mappingId: string): () => void;
}

const MappingTable: React.FC<Props> = ({
  mappings,
  testMapping,
  syncMappingLoading,
  handleTestMapping,
  handleCloseErrorMessage,
  handleSyncMapping,
  openEditMappingDialog,
  handleOpenDeleteDialog,
}) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  return (
    <div className={classes.tableWrapper}>
      {testMapping.errorMessage && (
        <Alert severity="error" onClose={handleCloseErrorMessage} className={classes.alert}>
          {testMapping.errorMessage}
        </Alert>
      )}

      <TableContainer className={classes.tableContainer}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '18%' }}>{t('schemaName')}</TableCell>
              <TableCell sx={{ width: '18%' }}>{t('funnelId')}</TableCell>
              <TableCell sx={{ width: '18%' }}>{t('occupations')}</TableCell>
              <TableCell sx={{ width: '18%' }}>{t('visitedWebsites')}</TableCell>
              <TableCell sx={{ width: '18%' }}>{t('lastUpdate')}</TableCell>
              <TableCell sx={{ width: '10%' }}>{t('actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {mappings.length > 0 ? (
              mappings.map((mapping) => (
                <TableRow key={mapping._id}>
                  <TableCell>{mapping.schemaName}</TableCell>
                  <TableCell>{mapping.funnelId}</TableCell>
                  <TableCell>{mapping.occupations.join(', ')}</TableCell>
                  <TableCell>{mapping.visitedWebsites.join(', ')}</TableCell>
                  <TableCell>{format(new Date(mapping.lastUpdateDate), 'MMMM d, yyyy h:mm a')}</TableCell>
                  <TableCell>
                    <Stack direction={'row'} gap={1}>
                      <IconButton className={classes.iconButton} onClick={() => handleSyncMapping(mapping._id!)} disabled={syncMappingLoading}>
                        <Sync />
                      </IconButton>
                      <IconButton className={classes.iconButton} onClick={() => handleTestMapping(mapping._id!)} disabled={testMapping.loading}>
                        <PlayArrow />
                      </IconButton>

                      <Divider orientation="vertical" flexItem />

                      <IconButton className={classes.iconButton} onClick={openEditMappingDialog(mapping)}>
                        <Edit />
                      </IconButton>
                      <IconButton className={cx(classes.iconButton, 'delete-button')} onClick={handleOpenDeleteDialog(mapping._id!)}>
                        <Delete />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  {t('noMappingsAvailable')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  tableWrapper: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  tableContainer: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: '8px',
    '& th': {
      border: 'none',
      padding: '4px 16px',
      fontSize: theme.typography.pxToRem(13),
      backgroundColor: theme.palette.background.paper,
      whiteSpace: 'nowrap',
      fontWeight: 'bold',
    },
    '& td, th': {
      border: 'none',
      borderBottom: '1px solid #ddd',
      padding: '8px 16px',
      textAlign: 'left',
      verticalAlign: 'middle',
      fontSize: theme.typography.pxToRem(13),
    },
    '& tr:last-child td': {
      borderBottom: 'none',
    },
  },
  iconButton: {
    padding: '4px',
    '& .MuiSvgIcon-root': {
      fontSize: theme.typography.pxToRem(20),
    },
    '&:hover': {
      '& .MuiSvgIcon-root': {
        color: theme.palette.primary.main,
      },
      '&.delete-button': {
        '& .MuiSvgIcon-root': {
          color: theme.palette.error.main,
        },
      },
    },
  },
  alert: {
    marginBottom: '16px',
    borderRadius: '8px',
  },
}));

export default MappingTable;
